import { type ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import '../datePickerStyles.css';
import { addMonths, endOfWeek, startOfWeek, subMonths } from 'date-fns';
import type { OuterButton, SwipeDirection } from '../datePickerTypes';
import {
  SessionStorageItems,
  SessionStorageService,
} from '../../../services/sessionStorageService';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { Skeleton } from '@mui/material';
import { useAppDispatch } from '../../../../../app/store';
import { summaryApi } from '../../../api/summaryApi';
import type { ITrackedTime } from '../../../models/IEmployeeTrackedTime';
import {
  findWeekTrackedTime,
  formatTrackedTimePreviewLine,
  parseTrackedTimeDates,
  pickTrackedTimeBucket,
} from '../../../utils/summaryDisplayUtils';

type Week = {
  firstDay: Date;
  lastDay: Date;
};

type WeekPickerProps = {
  onChange?: (week: Week) => void;
  disabled?: boolean;
  useLocalTime: boolean;
  clientId?: number;
  showTrackedTimePreview?: boolean;
  currencySymbol?: string;
  currencyCode?: string;
  initialWeek?: Week;
};

const calculateDisplayedDates = (monthDate: Date, cellsCount: number): Date[] => {
  const getDaysCount = (d: Date) => new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();

  const currentMonthDays = getDaysCount(monthDate);
  const prevMonthDays = getDaysCount(subMonths(monthDate, 1));

  const firstDayOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  let startDay = firstDayOfMonth.getDay() || 7;
  if (startDay === 1) startDay += 7;

  const allDates: Date[] = [];

  for (let i = startDay - 1; i > 0; i--) {
    allDates.push(
      new Date(monthDate.getFullYear(), monthDate.getMonth() - 1, prevMonthDays - i + 1)
    );
  }

  for (let i = 1; i <= currentMonthDays; i++) {
    allDates.push(new Date(monthDate.getFullYear(), monthDate.getMonth(), i));
  }

  const totalCells = allDates.length;
  let hoursColumnAdded = 0;
  for (let i = 1; i + hoursColumnAdded <= cellsCount - totalCells; i++) {
    const tempDate = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, i);
    if (tempDate.getDay() === 1) break;
    allDates.push(tempDate);
    if (tempDate.getDay() === 0) hoursColumnAdded++;
  }

  return allDates;
};

export const WeekPicker = ({
  onChange,
  disabled,
  useLocalTime,
  clientId,
  showTrackedTimePreview = false,
  currencySymbol = '',
  currencyCode,
  initialWeek: initialWeekProp,
}: WeekPickerProps) => {
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const [initialWeek] = useState<{ firstDay: Date; lastDay: Date }>(() => {
    return (
      initialWeekProp ??
      SessionStorageService.getDatePickerLastDateRangeOrDefault(
        SessionStorageItems.weekPickerLastDateRange
      )
    );
  });
  const [date, setDate] = useState(initialWeek.firstDay);
  const [selectedDay, setSelectedDay] = useState<Date>(initialWeek.firstDay);
  const [selectedWeek, setSelectedWeek] = useState<Week>({
    firstDay: initialWeek.firstDay,
    lastDay: initialWeek.lastDay,
  });
  const [data, setData] = useState<ITrackedTime[]>();
  const [fetchedRanges, setFetchedRanges] = useState<{ from: Date; to: Date }[]>([]);
  const [navigationLoading, setNavigationLoading] = useState(false);

  const countActiveRequest = useRef(0);
  const CELLS_COUNT = showTrackedTimePreview ? 48 : 42;
  const columnsPerRow = showTrackedTimePreview ? 8 : 7;

  const displayedDates = useMemo(
    () => (showTrackedTimePreview ? calculateDisplayedDates(date, CELLS_COUNT) : []),
    [CELLS_COUNT, date, showTrackedTimePreview]
  );

  const fetchRange = useMemo(() => {
    if (displayedDates.length === 0) {
      return null;
    }

    return {
      from: new Date(Math.min(...displayedDates.map((d) => d.getTime()))),
      to: new Date(Math.max(...displayedDates.map((d) => d.getTime()))),
    };
  }, [displayedDates]);

  const isRangeCached = useMemo(() => {
    if (!fetchRange) {
      return true;
    }

    return fetchedRanges.some(
      (range) => fetchRange.from >= range.from && fetchRange.to <= range.to
    );
  }, [fetchRange, fetchedRanges]);

  const needsFetch = showTrackedTimePreview && !!fetchRange && !!currencyCode && !isRangeCached;
  const isLoading =
    showTrackedTimePreview && (navigationLoading || (needsFetch && data === undefined));

  const getMonthName = (d: Date) => d.toLocaleDateString('en-US', { month: 'long' });
  const getDaysCount = (d: Date) => new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();

  useEffect(() => {
    SessionStorageService.setDatePickerLastDateRange(
      selectedWeek.firstDay,
      selectedWeek.lastDay,
      SessionStorageItems.weekPickerLastDateRange
    );
  }, [selectedWeek]);

  useEffect(() => {
    if (!needsFetch || !fetchRange) {
      return;
    }

    const requestId = ++countActiveRequest.current;
    let cancelled = false;
    const { from: fromDate, to: toDate } = fetchRange;

    dispatch(
      summaryApi.endpoints.getTrackedTimePerWeek.initiate({
        start: fromDate,
        end: toDate,
        useLocalTime,
        clientId,
      })
    )
      .unwrap()
      .then((result) => {
        if (cancelled || requestId !== countActiveRequest.current) {
          return;
        }

        const parsed = parseTrackedTimeDates(pickTrackedTimeBucket(result, currencyCode));

        setData((prev) =>
          prev === undefined
            ? parsed
            : parsed
                .filter(
                  (item) =>
                    !prev.some(
                      (existing) => existing.Date.toDateString() === item.Date.toDateString()
                    )
                )
                .concat(prev)
        );

        setFetchedRanges((prev) => [...prev, { from: fromDate, to: toDate }]);
      })
      .catch(() => {
        // Preview falls back to zero values when the request fails.
      })
      .finally(() => {
        if (!cancelled && requestId === countActiveRequest.current) {
          setNavigationLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [clientId, currencyCode, dispatch, fetchRange, needsFetch, useLocalTime]);

  const formatDateRange = () => {
    const monthFormatter = new Intl.DateTimeFormat('en-US', { month: 'short' });

    const day = selectedDay.getDay();
    const diffToMonday = day === 0 ? -6 : 1 - day;

    const monday = new Date(selectedDay);
    monday.setDate(selectedDay.getDate() + diffToMonday);

    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);

    return `${monthFormatter.format(monday)} ${monday.getDate()}${monday.getFullYear() !== sunday.getFullYear() ? ', ' + monday.getFullYear() : ''} - ${monday.getMonth() !== sunday.getMonth() ? monthFormatter.format(sunday) + ' ' : ''}${sunday.getDate()}, ${sunday.getFullYear()}`;
  };

  const handleMonthChange = (next: boolean) => {
    if (showTrackedTimePreview) {
      countActiveRequest.current++;
      setNavigationLoading(true);
    }

    setDate(next ? addMonths(date, 1) : subMonths(date, 1));
  };

  const handleDataSelection = (selectedDate: Date, swipeDirection: SwipeDirection) => {
    setSelectedDay(selectedDate);
    const week = {
      firstDay: startOfWeek(selectedDate, { weekStartsOn: 1 }),
      lastDay: endOfWeek(selectedDate, { weekStartsOn: 1 }),
    };
    setSelectedWeek({ ...week });
    if (swipeDirection != 'none') {
      handleMonthChange(selectedDate.getMonth() > date.getMonth());
    }

    if (onChange) {
      onChange(week);
    }
    setOpen(false);
  };

  const isSelectedWeek = (day: Date, otherDay: Date) => {
    const firstDayofSelectedWeek = startOfWeek(day, { weekStartsOn: 1 });
    const firstDayOfOtherDay = startOfWeek(otherDay, { weekStartsOn: 1 });
    return firstDayofSelectedWeek.toISOString() === firstDayOfOtherDay.toISOString();
  };

  const handleOuterButton = (buttonType: OuterButton): void => {
    const newDate = new Date(
      selectedDay.getFullYear(),
      selectedDay.getMonth(),
      selectedDay.getDate()
    );
    let swipeDirection: SwipeDirection = 'none';
    if (buttonType === 'prev') {
      newDate.setDate(newDate.getDate() - 7);
      if (selectedDay.getMonth() > newDate.getMonth()) swipeDirection = 'left';
    } else {
      newDate.setDate(newDate.getDate() + 7);
      if (selectedDay.getMonth() < newDate.getMonth()) swipeDirection = 'right';
    }
    handleDataSelection(newDate, swipeDirection);
  };

  const renderPrice = (targetDate: Date) => {
    if (isLoading) {
      return <Skeleton variant="rectangular" width={50} height={15} />;
    }

    const match = findWeekTrackedTime(data, targetDate);
    return formatTrackedTimePreviewLine(match?.Price, match?.TotalHours, currencySymbol);
  };

  const renderDays = () => {
    const currentMonthDays = getDaysCount(date);
    const prevMonthDays = getDaysCount(subMonths(date, 1));

    const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    let startDay = firstDayOfMonth.getDay() || 7;
    if (startDay === 1) startDay += 7;

    const prevMonth: ReactElement[] = [];
    for (let i = startDay - 1; i > 0; i--) {
      const tempDate = new Date(date.getFullYear(), date.getMonth() - 1, prevMonthDays - i + 1);
      const isSelected = isSelectedWeek(selectedDay, tempDate);
      let cellClass = 'calendar-cell other-date';
      if (isSelected) cellClass += ' selected-data-other-date';
      prevMonth.push(
        <td
          key={`${isSelected ? 'selected-prev' : 'prev'}-${prevMonthDays - i + 1}`}
          className={cellClass}
          onClick={() => handleDataSelection(tempDate, 'left')}
        >
          <span>{prevMonthDays - i + 1}</span>
        </td>
      );

      if (showTrackedTimePreview && tempDate.getDay() === 0) {
        prevMonth.push(
          <td
            key={`prev-hours-${i}`}
            className="calendar-cell datepicker-hours-cell"
            date-data={tempDate.toISOString()}
          >
            {renderPrice(tempDate)}
          </td>
        );
      }
    }

    const currentMonth: ReactElement[] = [];
    for (let i = 1; i <= currentMonthDays; i++) {
      const tempDate = new Date(date.getFullYear(), date.getMonth(), i);
      const isSelected = isSelectedWeek(selectedDay, tempDate);
      const isToday = new Date().toDateString() === tempDate.toDateString();

      let cellClass = 'calendar-cell ';
      if (isToday) cellClass += 'today';

      currentMonth.push(
        <td
          key={`${isSelected ? 'selected-current' : 'current'}-${i}`}
          className={cellClass}
          onClick={() => handleDataSelection(tempDate, 'none')}
        >
          <span>{i}</span>
        </td>
      );

      if (showTrackedTimePreview && tempDate.getDay() === 0) {
        currentMonth.push(
          <td
            key={`current-hours-${i}`}
            className="calendar-cell datepicker-hours-cell"
            date-data={tempDate.toISOString()}
          >
            {renderPrice(tempDate)}
          </td>
        );
      }
    }

    const totalCells = prevMonth.length + currentMonth.length;
    const nextMonth: ReactElement[] = [];
    let hoursColumnAdded = 0;
    for (let i = 1; i + hoursColumnAdded <= CELLS_COUNT - totalCells; i++) {
      const tempDate = new Date(date.getFullYear(), date.getMonth() + 1, i);
      if (tempDate.getDay() === 1) break;
      const isSelected = isSelectedWeek(selectedDay, tempDate);
      let cellClass = 'calendar-cell other-date';
      if (isSelected) cellClass = 'calendar-cell other-date selected-data-other-date';
      nextMonth.push(
        <td
          key={`${isSelected ? 'selected-next' : 'next'}-${i}`}
          className={cellClass}
          onClick={() => handleDataSelection(tempDate, 'right')}
        >
          <span>{i}</span>
        </td>
      );

      if (showTrackedTimePreview && tempDate.getDay() === 0) {
        nextMonth.push(
          <td
            key={`next-hours-${i}`}
            className="calendar-cell other-date datepicker-hours-cell"
            date-data={tempDate.toISOString()}
          >
            {renderPrice(tempDate)}
          </td>
        );
        hoursColumnAdded++;
      }
    }

    const cells = [...prevMonth, ...currentMonth, ...nextMonth];
    const rows: ReactElement[] = [];

    for (let rowStart = 0; rowStart < cells.length; rowStart += columnsPerRow) {
      const rowCells = cells.slice(rowStart, rowStart + columnsPerRow);
      const hasSelected = rowCells.some((cell) => cell.key?.toString().includes('selected'));
      rows.push(
        <tr className={`state-hover${hasSelected ? ' selected-data' : ''}`} key={`row-${rowStart}`}>
          {rowCells}
        </tr>
      );
    }

    return rows;
  };

  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  return (
    <>
      <div
        className="picker-mask"
        style={{ display: open ? 'block' : 'none' }}
        onClick={() => setOpen(false)}
      />
      <div className="date-picker-toolbar">
        <a
          id="prevOuterButton"
          className="datepicker-button datepicker-button-normal datepicker-button-mode-contained datepicker-widget datepicker-button-has-icon datepicker-calendar-navigator-previous-view datepicker-calendar-navigator-previous-month custom-date-picker-calendar-left"
          style={{ border: 'none' }}
          onClick={() => !disabled && handleOuterButton('prev')}
        >
          <div className="datepicker-button-content">
            <ArrowBackIosNewIcon className="datepicker-icon" fontSize="inherit" />
          </div>
        </a>
        <div className="custom-date-picker-container">
          <div className="custom-date-picker-container-text-container">
            <input
              className="custom-date-picker-container-text"
              type="text"
              value={formatDateRange()}
              readOnly
            />
          </div>
          <div className="custom-date-picker-container-button">
            <div className="calendar-icon-container" onClick={() => !disabled && setOpen(!open)}>
              <CalendarMonthOutlinedIcon className="calendar-icon" />
            </div>
          </div>
          {open && (
            <div
              ref={popupRef}
              className="datepicker-overlay-content mobile-date-picker datepicker-popup-normal datepicker-resizable datepicker-popup-inherit-height custom-date-picker-content"
            >
              <div className="datepicker-popup-content custom-date-picker-popup-content">
                <div className="custom-date-picker-content-calendar">
                  <div className="datepicker-widget datepicker-calendar-navigator">
                    <a
                      id="prev"
                      className="datepicker-button datepicker-button-normal datepicker-button-mode-contained datepicker-widget datepicker-button-has-icon datepicker-calendar-navigator-previous-view datepicker-calendar-navigator-previous-month custom-date-picker-calendar-left"
                      onClick={() => handleMonthChange(false)}
                    >
                      <div className="datepicker-button-content">
                        <ArrowBackIosNewIcon className="datepicker-icon" fontSize="inherit" />
                      </div>
                    </a>
                    <a className="datepicker-calendar-caption-button datepicker-button datepicker-button-normal datepicker-button-mode-contained datepicker-widget datepicker-button-has-text week-calendar-center">
                      <div className="datepicker-button-content">
                        <span className="datepicker-button-text">{`${getMonthName(date)} ${date.getFullYear()}`}</span>
                      </div>
                    </a>
                    <a
                      id="next"
                      className="datepicker-button datepicker-button-normal datepicker-button-mode-contained datepicker-widget datepicker-button-has-icon datepicker-calendar-navigator-next-view datepicker-calendar-navigator-next-month custom-date-picker-calendar-right"
                      onClick={() => handleMonthChange(true)}
                    >
                      <div className="datepicker-button-content">
                        <ArrowForwardIosIcon className="datepicker-icon" fontSize="inherit" />
                      </div>
                    </a>
                  </div>
                  <div className="calendar-body">
                    <div className="datepicker-calendar-views-wrapper">
                      <div className="datepicker-widget calendar-table">
                        <table>
                          <thead>
                            <tr>
                              <th>Mon</th>
                              <th>Tue</th>
                              <th>Wed</th>
                              <th>Thu</th>
                              <th>Fri</th>
                              <th>Sat</th>
                              <th>Sun</th>
                            </tr>
                          </thead>
                          <tbody>{renderDays()}</tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <a
          id="nextOuterButton"
          className="datepicker-button datepicker-button-normal datepicker-button-mode-contained datepicker-widget datepicker-button-has-icon datepicker-calendar-navigator-next-view datepicker-calendar-navigator-next-month custom-date-picker-calendar-right"
          style={{ border: 'none' }}
          onClick={() => !disabled && handleOuterButton('next')}
        >
          <div className="datepicker-button-content">
            <ArrowForwardIosIcon className="datepicker-icon" fontSize="inherit" />
          </div>
        </a>
      </div>
    </>
  );
};
